// src/services/gcsTimetableService.js
import localforage from 'localforage';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { periodCodeToTiming } from '../constants/periodCodes';

const CACHE_VERSION = 'gcsV3';
const CACHE_TTL = 3600000; // 1 hour in milliseconds
// Use proxy route to avoid CORS issues during development
const GCS_URL = process.env.NODE_ENV === 'development' 
  ? '/gcs/timetable-info/courses_detailed.json'
  : 'https://storage.googleapis.com/timetable-info/courses_detailed.json';

/**
 * Fetch timetable data from Firestore collection
 * @param {string} collectionName - The Firestore collection name
 * @param {string} termType - The term type for logging
 * @returns {Promise<Object>} Object containing courses and cache status
 */
const fetchDataFromFirestore = async (collectionName, termType) => {
  try {
    console.log(`Fetching ${termType} data from Firestore collection: ${collectionName}`);
    const db = getFirestore();
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);
    
    // Transform the data - Handle BOTH Firestore structure AND GCS structure
    const transformedCourses = snapshot.docs.map((doc, index) => {
      const data = doc.data();
      
      // Flexible field extraction - works with any field name variation
      const subj = data.Subj || data.subj || data.subject || data.Subject || '';
      const num = data.Num || data.num || data.number || data.Number || '';
      // Section: Use provided value, or default to '01' if missing/empty
      const secRaw = data.Sec || data.sec || data.section || data.Section || '';
      const sec = secRaw || '01'; // Default to '01' if section is missing or empty
      const title = data.Title || data.title || '';
      const periodCode = data['Period Code'] || data.period || data.Period || 'ARR';
      const room = data.Room || data.room || '';
      const building = data.Building || data.building || '';
      const instructor = data.Instructor || data.instructor || '';
      const xlist = data.XList || data.xlist || data.crossList || '';
      const wc = data.WC || data.wc || data.worldCulture || '';
      const dist = data.Dist || data.dist || data.distributive || '';
      
      const timing = periodCodeToTiming[periodCode] || 'Unknown Timing';
      
      // Create consistent documentName
      const documentName = (subj && num && sec) ? `${subj}${num}_${sec}` : doc.id;
      
      if (index < 3) {
        console.log(`${termType} course ${index} from Firestore:`, {
          raw: data,
          transformed: { subj, num, sec: `${sec}${secRaw ? '' : ' (defaulted to 01)'}`, title, periodCode }
        });
      }

      return {
        documentName: documentName,
        subj: subj,
        num: num,
        sec: sec,
        title: title,
        period: periodCode,
        timing: timing,
        room: room,
        building: building,
        instructor: instructor,
        xlist: xlist,
        wc: wc,
        dist: dist,
        isNotified: false
      };
    });

    console.log(`Fetched ${transformedCourses.length} ${termType} courses from Firestore`);
    if (transformedCourses.length > 0) {
      console.log(`First ${termType} course sample:`, transformedCourses[0]);
    }
    return transformedCourses;
  } catch (error) {
    console.error(`Error fetching ${termType} data from Firestore:`, error);
    throw error;
  }
};

/**
 * Fetch winter timetable data from Firestore
 * @returns {Promise<Object>} Object containing courses and cache status
 */
const fetchWinterDataFromFirestore = async () => {
  return fetchDataFromFirestore('winterTimetable26', 'winter');
};

/**
 * Fetch spring 2026 timetable data from Firestore
 * @returns {Promise<Object>} Object containing courses and cache status
 */
const fetchSpringDataFromFirestore = async () => {
  return fetchDataFromFirestore('springTimetable26', 'spring');
};

/**
 * Fetch spring 2025 timetable data from Firestore
 * @returns {Promise<Object>} Object containing courses and cache status
 */
const fetchSpring25DataFromFirestore = async () => {
  return fetchDataFromFirestore('springTimetable', 'spring25');
};

/**
 * Fetch timetable data from Google Cloud Storage or Firestore (for winter/spring)
 * @param {string} termType - The term type (summer, fall, winter, spring)
 * @returns {Promise<Object>} Object containing courses and cache status
 */
export const fetchGCSTimetableData = async (termType = 'winter') => {
  try {
    // For winter term, fetch from Firestore instead of GCS
    if (termType === 'winter') {
      // Check cache first
      const cacheKey = `winterCachedCourses`;
      const cacheTimestampKey = `winterCacheTimestamp`;
      const cacheVersionKey = `winterCacheVersion`;
      
      const cachedCourses = await localforage.getItem(cacheKey);
      const cacheTimestamp = await localforage.getItem(cacheTimestampKey);
      const cachedVersion = await localforage.getItem(cacheVersionKey);
      const now = Date.now();

      // Check if cache is valid
      const isCacheValid = 
        cachedCourses && 
        cacheTimestamp && 
        cachedVersion === CACHE_VERSION && 
        (now - cacheTimestamp) < CACHE_TTL;

      if (isCacheValid) {
        console.log('Using cached winter data');
        return {
          courses: cachedCourses,
          fromCache: true
        };
      }

      console.log('Cache invalid or expired, fetching new winter data from Firestore');

      // Fetch from Firestore
      const winterCourses = await fetchWinterDataFromFirestore();

      // Store in cache
      await Promise.all([
        localforage.setItem(cacheKey, winterCourses),
        localforage.setItem(cacheTimestampKey, now),
        localforage.setItem(cacheVersionKey, CACHE_VERSION)
      ]);

      console.log('Winter data cached');
      return {
        courses: winterCourses,
        fromCache: false
      };
    }

    // For spring term, fetch from Firestore
    if (termType === 'spring') {
      // Check cache first
      const cacheKey = `springCachedCourses`;
      const cacheTimestampKey = `springCacheTimestamp`;
      const cacheVersionKey = `springCacheVersion`;
      
      const cachedCourses = await localforage.getItem(cacheKey);
      const cacheTimestamp = await localforage.getItem(cacheTimestampKey);
      const cachedVersion = await localforage.getItem(cacheVersionKey);
      const now = Date.now();

      // Check if cache is valid
      const isCacheValid = 
        cachedCourses && 
        cacheTimestamp && 
        cachedVersion === CACHE_VERSION && 
        (now - cacheTimestamp) < CACHE_TTL;

      if (isCacheValid) {
        console.log('Using cached spring data');
        return {
          courses: cachedCourses,
          fromCache: true
        };
      }

      console.log('Cache invalid or expired, fetching new spring data from Firestore');

      // Fetch from Firestore
      const springCourses = await fetchSpringDataFromFirestore();

      // Store in cache
      await Promise.all([
        localforage.setItem(cacheKey, springCourses),
        localforage.setItem(cacheTimestampKey, now),
        localforage.setItem(cacheVersionKey, CACHE_VERSION)
      ]);

      console.log('Spring data cached');
      return {
        courses: springCourses,
        fromCache: false
      };
    }

    // For spring 2025 term, fetch from Firestore
    if (termType === 'spring25') {
      // Check cache first
      const cacheKey = `spring25CachedCourses`;
      const cacheTimestampKey = `spring25CacheTimestamp`;
      const cacheVersionKey = `spring25CacheVersion`;
      
      const cachedCourses = await localforage.getItem(cacheKey);
      const cacheTimestamp = await localforage.getItem(cacheTimestampKey);
      const cachedVersion = await localforage.getItem(cacheVersionKey);
      const now = Date.now();

      // Check if cache is valid
      const isCacheValid = 
        cachedCourses && 
        cacheTimestamp && 
        cachedVersion === CACHE_VERSION && 
        (now - cacheTimestamp) < CACHE_TTL;

      if (isCacheValid) {
        console.log('Using cached spring 2025 data');
        return {
          courses: cachedCourses,
          fromCache: true
        };
      }

      console.log('Cache invalid or expired, fetching new spring 2025 data from Firestore');

      // Fetch from Firestore
      const spring25Courses = await fetchSpring25DataFromFirestore();

      // Store in cache
      await Promise.all([
        localforage.setItem(cacheKey, spring25Courses),
        localforage.setItem(cacheTimestampKey, now),
        localforage.setItem(cacheVersionKey, CACHE_VERSION)
      ]);

      console.log('Spring 2025 data cached');
      return {
        courses: spring25Courses,
        fromCache: false
      };
    }

    // For fall/summer, use GCS
    // First check if we have cached data
    const cacheKey = `gcsCachedCourses_${termType}`;
    const cacheTimestampKey = `gcsCacheTimestamp_${termType}`;
    const cacheVersionKey = `gcsCacheVersion_${termType}`;
    
    const cachedCourses = await localforage.getItem(cacheKey);
    const cacheTimestamp = await localforage.getItem(cacheTimestampKey);
    const cachedVersion = await localforage.getItem(cacheVersionKey);
    const now = Date.now();

    // Check if cache is valid
    const isCacheValid = 
      cachedCourses && 
      cacheTimestamp && 
      cachedVersion === CACHE_VERSION && 
      (now - cacheTimestamp) < CACHE_TTL;

    if (isCacheValid) {
      console.log(`Using cached GCS ${termType} data`);
      return {
        courses: cachedCourses,
        fromCache: true
      };
    }

    console.log(`Cache invalid or expired, fetching new GCS ${termType} data`);

    // If cache version doesn't match, clear the specific cache entries
    if (cachedVersion !== CACHE_VERSION) {
      console.log('GCS Version mismatch, clearing cache');
      await localforage.removeItem(cacheKey);
      await localforage.removeItem(cacheTimestampKey);
      await localforage.removeItem(cacheVersionKey);
    }

    // Fetch new data from GCS
    const response = await fetch(GCS_URL);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch timetable data: ${response.status}`);
    }

    const rawData = await response.json();
    
    if (!Array.isArray(rawData)) {
      throw new Error('Invalid data format: expected array');
    }

    // Transform the data to match the expected format
    const transformedCourses = rawData.map((course, index) => {
      const periodCode = course['Period Code'];
      const timing = periodCodeToTiming[periodCode] || 'Unknown Timing';

      return {
        documentName: `${course.Subj}${course.Num}_${course.Sec}`,
        subj: course.Subj,
        num: course.Num,
        sec: course.Sec,
        title: course.Title,
        period: periodCode,
        timing: timing,
        room: course.Room,
        building: course.Building,
        instructor: course.Instructor,
        xlist: course.XList,
        wc: course.WC,
        dist: course.Dist,
        isNotified: false
      };
    });

    // Store the new data in cache
    await Promise.all([
      localforage.setItem(cacheKey, transformedCourses),
      localforage.setItem(cacheTimestampKey, now),
      localforage.setItem(cacheVersionKey, CACHE_VERSION)
    ]);

    console.log(`New GCS ${termType} data cached`);
    return {
      courses: transformedCourses,
      fromCache: false
    };
  } catch (error) {
    console.error(`Error fetching GCS timetable data:`, error);
    throw error;
  }
};

/**
 * Clear GCS timetable cache
 * @param {string} termType - The term type to clear cache for
 */
export const clearGCSCache = async (termType = 'winter') => {
  try {
    // Spring and winter use different cache key patterns
    let cacheKey, cacheTimestampKey, cacheVersionKey;
    
    if (termType === 'winter') {
      cacheKey = 'winterCachedCourses';
      cacheTimestampKey = 'winterCacheTimestamp';
      cacheVersionKey = 'winterCacheVersion';
    } else if (termType === 'spring') {
      cacheKey = 'springCachedCourses';
      cacheTimestampKey = 'springCacheTimestamp';
      cacheVersionKey = 'springCacheVersion';
    } else if (termType === 'spring25') {
      cacheKey = 'spring25CachedCourses';
      cacheTimestampKey = 'spring25CacheTimestamp';
      cacheVersionKey = 'spring25CacheVersion';
    } else {
      // Fall/summer use the generic pattern
      cacheKey = `gcsCachedCourses_${termType}`;
      cacheTimestampKey = `gcsCacheTimestamp_${termType}`;
      cacheVersionKey = `gcsCacheVersion_${termType}`;
    }
    
    await Promise.all([
      localforage.removeItem(cacheKey),
      localforage.removeItem(cacheTimestampKey),
      localforage.removeItem(cacheVersionKey)
    ]);
    
    console.log(`Cache cleared for ${termType}`);
  } catch (error) {
    console.error(`Error clearing cache for ${termType}:`, error);
  }
};
